import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getDocumentReport } from 'lib/api/reports';
import { Dispatch } from 'redux';

import {
  Business,
  DocumentReport,
  FactoringExtension,
  FactoringQuoteWithDocument,
  Stakeholder,
} from './documentProfileSlice.types';

export type Document = Partial<DocumentReport> & {
  id?: string | null;
  amountConciliated?: number | null;
  amountConciliatedByCreditNotes?: number | null;
  amountConciliatedByCreditRequests?: number | null;
  amountConciliatedByDiscounts?: number | null;
  amountConciliatedByFactoring?: number | null;
  amountConciliatedByFunds?: number | null;
  amountConciliatedByPassives?: number | null;
  amountConciliatedByPayIns?: number | null;
  arrearConciliation?: number | null;
  documentConciliation?: number | null;
  documentRemainingAmount?: number | null;
  amountAdvanced?: number | null;
  documentAmountDiscounted?: number | null;
  interestCost?: number | null;
  costRate?: number | null;
  business?: Business;
  stakeholder?: Stakeholder;
  creditRequests?: any[];
  factoringQuotes?: FactoringQuoteWithDocument[];
  factoringExtensions?: FactoringExtension[];
  receiptDate?: Date | string | null;
  originalPaymentDate?: Date | string | null;
  amountToDepositTotal?: number | null;
  costs?: number | null;
  otherCosts?: number | null;
  income?: number | null;
  operationCostFixed?: number | null;
  operationCostVariable?: number | null;
  arrearStructure?: {
    arrearRate: number;
    arrearType: string;
    arrearSegments: any[];
  };
};

export type DocumentProfileState = {
  open: boolean;
  document: Document;
  loading: boolean;
  error: string | null;
  title?: string;
};

const initialState: DocumentProfileState = {
  open: false,
  document: {
    id: null,
  } as Document,
  loading: false,
  error: null,
  title: 'Detalle documento',
};

export const fetchDocumentProfile = async (dispatch: Dispatch, id: string) => {
  const response = await getDocumentReport(dispatch, id);
  return response;
};

export const loadDocumentProfile = async (dispatch: Dispatch, id: string) => {
  dispatch(setDocumentLoading(true));
  const response = await fetchDocumentProfile(dispatch, id);
  dispatch(setDocumentData(response));
  dispatch(setDocumentLoading(false));
};

const documentProfileSlice = createSlice({
  name: 'documentProfile',
  initialState,
  reducers: {
    showDocumentProfile: (
      state,
      action: PayloadAction<Document & { title?: string }>,
    ) => {
      state.open = true;
      state.document = { ...state.document, ...action.payload };
      state.title = action.payload.title || 'Detalle documento';
      state.error = null;
    },
    hideDocumentProfile: (state) => {
      state.open = false;
      state.document = { id: null } as Document;
      state.title = 'Detalle documento';
      state.error = null;
    },
    setDocumentLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setDocumentData: (state, action: PayloadAction<Document>) => {
      state.document = { ...state.document, ...action.payload };
      state.loading = false;
      state.error = null;
    },
    setDocumentError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    updateDocument: (state, action: PayloadAction<Partial<Document>>) => {
      state.document = { ...state.document, ...action.payload };
    },
  },
});

export const {
  showDocumentProfile,
  hideDocumentProfile,
  setDocumentLoading,
  setDocumentError,
  updateDocument,
  setDocumentData,
} = documentProfileSlice.actions;

export default documentProfileSlice.reducer;
